import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  CleanFiltersIcon,
  IconSearch,
  SearchUser,
} from "~misc/icons";
import { SelectArrow } from "./FilterSelect";

const defaultOrderOptions = [
  { value: "alfabetica", label: "Ordem alfabética" },
  { value: "mais-antigos", label: "Pedidos mais antigos" },
  { value: "mais-recentes", label: "Pedidos mais recentes" },
];

const renderFilterInput = (field) => {
  if (field.type === "date") {
    return (
      <input
        type="date"
        value={field.value ?? ""}
        onChange={(event) => field.onChange?.(event.target.value)}
        className="h-11 w-full rounded-md border border-[#C8CED8] bg-white px-3 text-[15px] font-semibold text-[#1A2B49] outline-none"
      />
    );
  }

  return (
    <div className="relative">
      <select
        value={field.value ?? ""}
        onChange={(event) => field.onChange?.(event.target.value)}
        className="h-11 w-full appearance-none rounded-md border border-[#C8CED8] bg-white px-3 pr-10 text-[15px] font-semibold text-[#1A2B49] outline-none"
      >
        {(field.options || []).map((option) => (
          <option key={`${field.id}-${option.value}`} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>

      <SelectArrow />
    </div>
  );
};

const ReportFilterBar = ({
  searchValue = "",
  searchPlaceholder =
    "Busque por Codigo de Cliente, Razao Social ou Numero do Pedido",
  onSearchValueChange,
  onSearchSubmit,
  filterFields = [],
  onClearFilters,
  showClearButton = true,
  showOrder = true,
  orderValue = "alfabetica",
  orderOptions = defaultOrderOptions,
  orderLabel = "Ordenar por:",
  onOrderChange,
  orderPlacement = "auto",
  className = "",
}) => {
  const [localSearchValue, setLocalSearchValue] = useState(searchValue);

  useEffect(() => {
    setLocalSearchValue(searchValue);
  }, [searchValue]);

  const hasFilterFields = filterFields.length > 0;

  const resolvedOrderPlacement = useMemo(() => {
    if (orderPlacement !== "auto") {
      return orderPlacement;
    }

    return hasFilterFields ? "bottom" : "top";
  }, [hasFilterFields, orderPlacement]);

  const handleSearchValueChange = useCallback((nextValue) => {
    setLocalSearchValue(nextValue);
    onSearchValueChange?.(nextValue);
  }, [onSearchValueChange]);

  const handleSubmit = useCallback(() => {
    onSearchSubmit?.(localSearchValue);
  }, [onSearchSubmit, localSearchValue]);

  const orderControl =
    showOrder && orderOptions.length > 0 ? (
      <div className="flex items-center gap-2">
        <span className="text-sm text-[#1A2B49]">{orderLabel}</span>

        <div className="relative">
          <select
            value={orderValue}
            onChange={(event) => onOrderChange?.(event.target.value)}
            className="h-11 appearance-none rounded-md bg-transparent pl-1 pr-11 text-[17px] font-bold text-[#050B5C] outline-none"
          >
            {orderOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          <span className="pointer-events-none absolute right-1 top-1/2 -translate-y-1/2">
            <svg width="8" height="5" viewBox="0 0 8 5" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M7 1.125L4 3.875L1 1.125" stroke="#01054C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
        </div>
      </div>
    ) : null;

  return (
    <section className={`container rounded-md  ${className}`}>
      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <SearchUser className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />

          <input
            value={localSearchValue}
            placeholder={searchPlaceholder}
            onChange={(event) => handleSearchValueChange(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                handleSubmit();
              }
            }}
            className="h-11 w-full rounded-md border border-[#C8CED8] bg-white pl-10 pr-11 text-sm text-[#1A2B49] outline-none"
          />

          <button
            type="button"
            onClick={handleSubmit}
            className="absolute right-3 top-1/2 -translate-y-1/2"
            aria-label="Pesquisar"
          >
            <IconSearch className="h-4 w-4" />
          </button>
        </div>

        {resolvedOrderPlacement === "top" && orderControl}
      </div>

      {hasFilterFields && (
        <div className="mt-3 grid grid-cols-1 gap-2 md:grid-cols-3">
          {filterFields.map((field) => (
            <div key={field.id}>
              <label className="mb-1 block text-xs text-[#1A2B49]">{field.label}</label>
              {renderFilterInput(field)}
            </div>
          ))}
        </div>
      )}

      {(showClearButton || (resolvedOrderPlacement === "bottom" && showOrder)) && (
        <div className="mt-3 flex flex-wrap items-center justify-end gap-4">
          {showClearButton && (
            <button
              type="button"
              onClick={onClearFilters}
              className="flex items-center gap-2 text-sm text-[#1A2B49]"
            >
              <CleanFiltersIcon className="h-8 w-8" />
              <span>Limpar filtros</span>
            </button>
          )}

          {resolvedOrderPlacement === "bottom" && orderControl}
        </div>
      )}
    </section>
  );
};

const MemoReportFilterBar = React.memo(ReportFilterBar);
export default MemoReportFilterBar;
